import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, ArrowRight, Send, AlertCircle, Shield } from 'lucide-react';

import { useRequestMagicLink, useOidcConfig } from '../../api/hooks/auth.hook';
import { type ApiErrorResponse, getOidcLoginUrl } from '../../api/services/auth';
import { useAuth } from '../../auth/AuthProvider';
import { cn } from '../../utils/cn';

const SUCCESS_MESSAGE =
  "We've sent a secure sign-in link to your inbox. Click the link to access your account.";

const resolveErrorMessage = (error: ApiErrorResponse | unknown): string => {
  if (!error || typeof error !== 'object') {
    return 'Could not connect to the server. Please check your internet connection.';
  }

  const status = (error as ApiErrorResponse).status;
  const errorCode =
    typeof (error as ApiErrorResponse).error === 'string'
      ? (error as ApiErrorResponse).error
      : undefined;

  if (status === 403) {
    return 'Access denied. This email is not on the invite list.';
  }

  if (status === 429 || errorCode === 'rate_limited') {
    return 'Too many requests. Please wait a moment before trying again.';
  }

  if (status && status >= 500) {
    return 'Service unavailable. Our team has been notified.';
  }

  return 'Unable to sign in. Please try again later.';
};

import StartupLogo from '../../components/common/Icons/startup_logo.png';

export const SignInPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { status } = useAuth();
  const [email, setEmail] = useState('');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [submissionState, setSubmissionState] = useState<'idle' | 'loading' | 'success' | 'error'>(
    'idle',
  );
  const [inlineError, setInlineError] = useState<string | null>(null);
  const requestLink = useRequestMagicLink();
  const oidcConfig = useOidcConfig();
  const keycloakEnabled = oidcConfig.data?.enabled ?? false;

  // Handle OIDC callback errors from query params
  useEffect(() => {
    const oidcError = searchParams.get('error');
    if (oidcError) {
      const errorMessages: Record<string, string> = {
        oidc_error: 'Single sign-on failed. Please try again.',
        missing_code: 'Authorization was not completed. Please try again.',
        invalid_state: 'Session expired. Please try signing in again.',
        token_exchange_failed: 'Could not complete sign-in. Please try again.',
        invalid_token: 'Authentication token was invalid. Please try again.',
        provisioning_failed: 'Could not set up your account. Please contact support.',
      };
      setSubmissionState('error');
      setStatusMessage(errorMessages[oidcError] ?? 'Sign-in failed. Please try again.');
    }
  }, [searchParams]);

  useEffect(() => {
    if (status === 'authenticated') {
      navigate('/');
    }
  }, [navigate, status]);

  const isEmailValid = useMemo(() => {
    const value = email.trim();
    if (!value) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }, [email]);

  const disabled = useMemo(
    () => submissionState === 'loading' || requestLink.isLoading || !email,
    [submissionState, requestLink.isLoading, email],
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isEmailValid) {
      setInlineError('Please enter a valid email address.');
      setSubmissionState('error');
      // Focus logic if needed
      return;
    }

    setInlineError(null);
    setSubmissionState('loading');
    setStatusMessage(null);
    try {
      await requestLink.mutateAsync({ email });
      setSubmissionState('success');
      setStatusMessage(SUCCESS_MESSAGE);
    } catch (submitError) {
      setSubmissionState('error');
      setStatusMessage(resolveErrorMessage(submitError));
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-gray-50 p-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-sm">
        {/* Card */}
        <div className="bg-white shadow-xl shadow-gray-200/50 rounded-2xl overflow-hidden border border-gray-100">
          <div className="p-8 pb-6">
            {/* Header */}
            <div className="text-center mb-8">
              <img
                src={StartupLogo}
                alt="insora"
                className="h-12 w-12 object-contain mx-auto mb-4"
              />
              <h2 className="text-2xl font-bold tracking-tight text-gray-900">Hey!</h2>
              <p className="mt-2 text-sm text-gray-500">Sign in to your insora workspace</p>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit} noValidate>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Work Email
                </label>
                <div className="relative rounded-md shadow-sm group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail
                      className={cn(
                        'h-5 w-5 transition-colors duration-200',
                        inlineError
                          ? 'text-red-400'
                          : 'text-gray-400 group-focus-within:text-blue-600',
                      )}
                    />
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (inlineError) setInlineError(null);
                    }}
                    disabled={submissionState === 'loading' || submissionState === 'success'}
                    className={cn(
                      'block w-full pl-10 pr-3 py-2.5 border rounded-xl text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 focus:border-transparent sm:text-sm sm:leading-6 transition-all duration-200',
                      inlineError
                        ? 'border-red-300 text-red-900 placeholder:text-red-300 focus:ring-red-500'
                        : 'border-gray-200 hover:border-gray-300',
                    )}
                    placeholder="you@company.com"
                    autoComplete="email"
                    autoFocus
                  />
                </div>
                {inlineError && (
                  <p className="mt-2 text-sm text-red-600 flex items-center animate-in slide-in-from-top-1 duration-200">
                    <AlertCircle className="h-4 w-4 mr-1.5" />
                    {inlineError}
                  </p>
                )}
              </div>

              {statusMessage && (
                <div
                  className={cn(
                    'rounded-xl p-4 text-sm flex items-start gap-3 animate-in fade-in zoom-in-95 duration-200',
                    submissionState === 'success'
                      ? 'bg-green-50 text-green-800 border border-green-100'
                      : 'bg-red-50 text-red-800 border border-red-100',
                  )}
                >
                  <div className="shrink-0 mt-0.5">
                    {submissionState === 'success' ? (
                      <Send className="h-5 w-5 text-green-600" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-600" />
                    )}
                  </div>
                  <p className="font-medium leading-relaxed">{statusMessage}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={disabled}
                className={cn(
                  'w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200',
                  submissionState === 'loading' && 'cursor-wait',
                )}
              >
                {submissionState === 'loading' ? (
                  <div className="h-5 w-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    Send Magic Link
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>

              {keycloakEnabled && (
                <>
                  <div className="relative my-2">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-white px-3 text-gray-400">or</span>
                    </div>
                  </div>

                  <a
                    href={getOidcLoginUrl()}
                    className="w-full flex justify-center items-center py-2.5 px-4 border border-gray-300 rounded-xl shadow-sm text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 transition-all duration-200"
                  >
                    <Shield className="mr-2 h-4 w-4" />
                    Sign in with SSO
                  </a>
                </>
              )}
            </form>
          </div>

          <div className="bg-gray-50 px-8 py-4 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-500">
              No password required. We'll send a secure link to sign in instantly.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              By using Insora in demo, you agree to our{' '}
              <a
                href="https://www.insora.dev/lang/en/terms.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline"
              >
                terms
              </a>
              .
            </p>
          </div>
        </div>

        {/* Footer Links */}
        <p className="mt-8 text-center text-xs text-gray-400">insora</p>
      </div>
    </div>
  );
};

export default SignInPage;
